import { useState } from "react";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { JOB_STEPS } from "../CreateJobPage";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";
import { ArrowDown, PlusIcon } from "lucide-react";
import CustomDropdown from "./CustomDropdown";
import FieldError from "./FieldError";
import {
  EMPTY_SKILL,
  JobFormValues,
  JobSkillFormValues,
  skillSchema,
} from "../schema/jobForm.schema";
import {
  SKILL_EXPERIENCE_OPTIONS,
  SKILL_IMPORTANCE_OPTIONS,
  SKILL_LEVEL_OPTIONS,
  SKILL_RECENCY_OPTIONS,
} from "../schema/jobForm.options";

const SKILL_SELECT_FIELDS: {
  name: Exclude<keyof JobSkillFormValues, "title" | "usageContext">;
  label: string;
  options: string[];
}[] = [
  { name: "importance", label: "Importance", options: SKILL_IMPORTANCE_OPTIONS },
  { name: "requiredLevel", label: "Required level", options: SKILL_LEVEL_OPTIONS },
  { name: "experience", label: "Minimum experience", options: SKILL_EXPERIENCE_OPTIONS },
  { name: "recency", label: "Recency", options: SKILL_RECENCY_OPTIONS },
];

interface SkillCardProps {
  skillIndex: number;
  onRemove: () => void;
}

const SkillCard = ({ skillIndex, onRemove }: SkillCardProps) => {
  const {
    control,
    register,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const skill = useWatch({ control, name: `skills.${skillIndex}` });
  const skillErrors = errors.skills?.[skillIndex];

  // Incomplete skills (new, imported or resumed) open in edit mode.
  const [isEditing, setIsEditing] = useState(
    () => !skillSchema.safeParse(getValues(`skills.${skillIndex}`)).success,
  );
  const [isSkillUsedExpanded, setIsSkillUsedExpanded] = useState(false);

  const handleSave = async () => {
    if (await trigger(`skills.${skillIndex}`, { shouldFocus: true })) {
      setIsEditing(false);
    }
  };

  //   Create/Edit
  if (isEditing)
    return (
      <div className="rounded-md border-l-[3px] border-r border-y border-l-slate-600 overflow-hidden">
        <div className="flex justify-between py-3 border-b px-4 bg-slate-100">
          <div className="flex items-center gap-4">
            <span className="text-xss font-bold text-light-black uppercase">
              {String(skillIndex + 1)?.padStart(2, "0")}
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-xsss font-bold tracking-wider text-light-black">
                SKILL REQUIREMENT
              </span>
              <CustomInputV2
                placeholder="e.g. Cloud security"
                inputContainerStyle={
                  skillErrors?.title ? "border-orange-700" : ""
                }
                {...register(`skills.${skillIndex}.title`)}
              />
              <FieldError message={skillErrors?.title?.message} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            {skill?.importance ? (
              <div className="rounded-full px-2 py-1 text-light-black bg-gray-200 text-xsss font-bold">
                {skill.importance}
              </div>
            ) : null}
            <button
              type="button"
              onClick={handleSave}
              className="border border-light-grey bg-gray-200 rounded-md font-bold text-xsss text-light-black px-3 hover:bg-slate-50 h-8"
            >
              Save Skill
            </button>
            <button
              type="button"
              onClick={onRemove}
              className="font-bold text-xsss text-light-black hover:bg-slate-50 rounded-md px-3 h-8"
            >
              Remove
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-4 px-4 py-3">
          <div className="grid grid-cols-4 gap-4 items-start">
            {SKILL_SELECT_FIELDS.map(({ name, label, options }) => (
              <div key={name} className="flex flex-col gap-1.5">
                <label className="text-xss font-bold text-light-black">
                  {label}
                </label>
                <Controller
                  control={control}
                  name={`skills.${skillIndex}.${name}`}
                  render={({ field, fieldState }) => (
                    <>
                      <CustomDropdown
                        options={options}
                        value={field.value}
                        onChange={field.onChange}
                        hasError={!!fieldState.error}
                      />
                      <FieldError message={fieldState.error?.message} />
                    </>
                  )}
                />
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-light-black">
              How will this skill be used in the role?
            </label>
            <textarea
              className={`border rounded-md text-xss font-medium text-light-black p-2 ${skillErrors?.usageContext ? "border-orange-700" : ""}`}
              placeholder="Describe the decisions, responsibilities or outcomes this skill supports."
              rows={5}
              {...register(`skills.${skillIndex}.usageContext`)}
            />
            <FieldError message={skillErrors?.usageContext?.message} />
          </div>
        </div>
      </div>
    );

  //   Read only
  return (
    <div className="rounded-md border-l-[3px] border-r border-y border-l-slate-600 overflow-hidden py-3.5 px-4">
      <div className="flex justify-between pb-2.5">
        <div className="flex flex-col">
          <span className="text-xsss font-bold text-light-black uppercase">
            {String(skillIndex + 1)?.padStart(2, "0")} / SKILL REQUIREMENT
          </span>
          <span className="text-md font-semibold text-black">
            {skill?.title}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="border border-slate-600 rounded-md font-bold text-xsss text-slate-600 px-3 hover:bg-slate-50 h-8"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="font-bold text-xsss text-light-black hover:bg-slate-50 rounded-md px-3 h-8"
          >
            Remove
          </button>
        </div>
      </div>

      <div className="grid grid-cols-[0.9fr_0.9fr_0.9fr_0.9fr_1.4fr] items-center border-y py-2.5">
        {SKILL_SELECT_FIELDS.map(({ name, label }, index) => (
          <div
            key={name}
            className={`flex flex-col flex-1 gap-0.5 ${index > 0 ? "border-l pl-4" : ""}`}
          >
            <span className="font-bold text-xsss tracking-wider uppercase text-light-black">
              {label}
            </span>
            <span className="text-xss font-bold text-black">
              {skill?.[name]}
            </span>
          </div>
        ))}

        <button
          type="button"
          onClick={() => setIsSkillUsedExpanded((prev) => !prev)}
          aria-expanded={isSkillUsedExpanded}
          className={`flex justify-between items-center ml-auto w-max gap-8 px-2 py-1 border rounded-md ${isSkillUsedExpanded ? "bg-slate-100 border-light-grey" : ""} hover:bg-slate-100`}
        >
          <div className="flex flex-col gap-0.5">
            <span className="font-bold text-xsss tracking-wider uppercase text-light-black text-left">
              Role-use Context
            </span>
            <span className="text-xsss font-bold text-black text-left">
              {isSkillUsedExpanded ? "Hide full text" : "View full text"}
            </span>
          </div>

          <div className="flex items-center justify-center w-6 h-6 rounded-full border bg-white shrink-0">
            <ArrowDown
              size={12}
              className={`transition-transform duration-200 ease-out ${
                isSkillUsedExpanded ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>
        </button>
      </div>

      {isSkillUsedExpanded ? (
        <div className="flex flex-col gap-1 p-4 mt-3 border-l-[3px] border-y border-r border-gray-200 rounded-r-md border-l-slate-600 bg-slate-50">
          <span className="font-bold text-xsss text-light-black tracking-wider uppercase">
            How this skill is used in the role
          </span>
          <span className="text-xss font-medium text-charcoal leading-4">
            {skill?.usageContext}
          </span>
        </div>
      ) : null}
    </div>
  );
};

const Step4 = ({ activeStep }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<JobFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: "skills" });

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {fields.length} requirement{fields.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="flex flex-col mt-5 gap-3">
        {fields.map((field, skillIndex) => {
          return (
            <SkillCard
              key={field.id}
              skillIndex={skillIndex}
              onRemove={() => remove(skillIndex)}
            />
          );
        })}
        <FieldError
          message={errors.skills?.root?.message ?? errors.skills?.message}
        />
        <button
          type="button"
          onClick={() => append({ ...EMPTY_SKILL })}
          className="flex items-center rounded-md border border-dashed bg-slate-50 font-bold text-slate-600 text-xss h-10 w-max px-3 gap-2 hover:bg-white"
        >
          <PlusIcon size={14} />
          Add another skill requirement
        </button>
      </div>
    </>
  );
};

export default Step4;
